import { TASK_STATUSES, sprintTypeOf } from '@nedelnik/shared';

import { SyncStatus } from '../SyncStatus.js';
import { haptic } from '../haptic.js';
import { Pencil, Plus, Times } from '../icons.js';
import { STATUS_LABELS } from '../sprint/labels.js';
import { suggestTags } from '../tags/tags.js';

import { Checklist } from './Checklist.js';
import { SprintPicker } from './SprintPicker.js';
import { DueField } from './Due.js';
import { Relations } from './Relations.js';
import { RepeatMenu, RepeatNext, repeatAvailable } from './RepeatMenu.js';
import { useAutosave } from './autosave.js';
import { useSheetDrag } from './sheet.js';

import { useEffect, useRef, useState } from 'react';

import type { CreateOutcome } from '../../core/actions.js';
import type { TagCount } from '../tags/tags.js';
import type {
  ChecklistItem,
  Repeat,
  SprintKey,
  Task,
  TaskLink,
  TaskStatus,
  TaskTag,
} from '@nedelnik/shared';

/**
 * Панель задачи (3.5).
 *
 * Открывается поверх списка и не уводит со спринта (3.5.1). Кнопки «Сохранить»
 * нет: правки уходят сами (3.5.2). Сверху то, что нужно почти всегда —
 * заголовок, статус, спринт, описание, теги; ниже то, что нужно иногда —
 * проект, дедлайн, чек-лист и связи (3.5.3).
 *
 * Компонент монтируется с `key={task.id}`: черновики полей живут в локальном
 * состоянии, и без пересоздания в открытой панели остался бы текст соседней
 * задачи.
 */
/** Поле ввода — то, из-за чего на телефоне выезжает клавиатура. */
function isTextField(target: EventTarget): boolean {
  return (
    target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
  );
}

export interface TaskPanelActions {
  setTitle: (task: Task, title: string) => void;
  setNotes: (task: Task, notes: string) => void;
  setStatus: (task: Task, status: TaskStatus) => void;
  setDue: (task: Task, due: string | null, dueTime: string | null) => void;
  setSprint: (task: Task, sprint: SprintKey | null) => void;
  setRepeat: (task: Task, repeat: Repeat | null) => void;
  remove: (task: Task) => void;
  /** Вложить в проект или вынуть; правила уровней проверяет действие (3.8.3). */
  setParent: (task: Task, parentId: string | null) => void;
  /**
   * Завести проект с этим названием и сразу вложить в него задачу.
   *
   * Возвращает исход, а не `void`: создание может не состояться (номера задач
   * ещё не приехали с сервера), и промолчать здесь значит показать человеку
   * нажатие без последствий и без объяснения.
   */
  createProject: (task: Task, name: string) => Promise<CreateOutcome>;
  /** Завести задачу и сразу вложить её в эту (3.8.10). */
  createSubtask: (task: Task, name: string) => Promise<CreateOutcome>;
  /** Завести задачу и сразу связать её с этой нужной стороной (3.9.2). */
  createLinked: (task: Task, name: string, side: 'depends' | 'blocks') => Promise<CreateOutcome>;
  addLink: (blockerId: string, blockedId: string) => void;
  removeLink: (link: TaskLink) => void;
  addTag: (taskId: string, raw: string) => void;
  removeTag: (tag: TaskTag) => void;
  addChecklistItem: (taskId: string, text: string, afterOrderKey: string | null) => void;
  toggleChecklistItem: (item: ChecklistItem) => void;
  setChecklistText: (item: ChecklistItem, text: string) => void;
  removeChecklistItem: (item: ChecklistItem) => void;
}

export function TaskPanel({
  task,
  tags,
  allTags,
  checklist,
  today,
  currentSprint,
  tasks,
  byId,
  links,
  actions,
  onOpenTask,
  onOpenProject,
  onClose,
}: {
  task: Task;
  tags: readonly TaskTag[];
  /** Все теги пользователя со счётчиками: из них подбираются подсказки (3.10.5). */
  allTags: readonly TagCount[];
  checklist: readonly ChecklistItem[];
  today: string;
  currentSprint: SprintKey;
  /** Все живые задачи: из них подбираются проект и связи (3.8.3, 3.9.2). */
  tasks: readonly Task[];
  byId: ReadonlyMap<string, Task>;
  links: readonly TaskLink[];
  actions: TaskPanelActions;
  onOpenTask: (task: Task) => void;
  onOpenProject: (projectId: string) => void;
  onClose: () => void;
}): React.ReactElement {
  const title = useAutosave(task.title ?? '', (next) => actions.setTitle(task, next));
  const notes = useAutosave(task.notes ?? '', (next) => actions.setNotes(task, next));
  const [typing, setTyping] = useState(false);
  // Потянуть вниз — закрыть (3.5.7). Пока печатают, жест выключен: палец в
  // это время ставит курсор и выделяет текст.
  const sheet = useSheetDrag({ enabled: !typing, onClose });

  // Esc закрывает панель — это поведение диалога, а не хоткей: у панели есть и
  // видимая кнопка закрытия, и подложка (принцип «у каждого хоткея есть
  // видимый элемент управления»).
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay">
      <button
        type="button"
        className="overlay__backdrop"
        aria-label="Закрыть задачу"
        onClick={onClose}
      />

      {/*
        Шапки у панели нет: заголовок в ней повторял поле «Название», которое
        теперь стоит первым и правится на месте, а крестик — кнопку «К задачам»
        внизу. Два способа сделать одно и то же на одном экране мы уже
        разбирали на этапе 11 (две кнопки «Закрыть»), и вывод тот же.

        Подвал при этом не уезжает с прокруткой: тело панели прокручивается
        само, поэтому выход виден всегда.
      */}
      <aside ref={sheet.ref} className="panel" role="dialog" aria-label="Задача">
        {/*
          Ручка шторки — только на телефоне (3.5.7): за неё карточку тянут
          вниз, даже когда она прокручена. Это картинка жеста, а не кнопка:
          закрыть карточку без жеста можно подложкой и «К задачам».
        */}
        <div className="panel__grip" aria-hidden="true" />
        {/*
          Пока человек печатает, подвал с «Удалить задачу» на телефоне только
          отнимает высоту у того, ради чего клавиатуру и открыли. Отслеживается
          именно фокус в поле, а не сам факт клавиатуры: узнать о ней у
          браузера нельзя, а фокус — ровно то же событие с точки зрения
          человека.
        */}
        <div
          className="panel__body"
          onFocusCapture={(event) => setTyping(isTextField(event.target))}
          onBlurCapture={() => setTyping(false)}
        >
          {/*
            Название — обычное поле с подписью, как «Описание» рядом, и с
            карандашом справа.

            Раньше это была `textarea` без подписи и почти без рамки: она
            схлопывалась в узкую полоску, в которую и попасть-то было нечем, а
            заголовок в ней читался как простой текст. Заголовок у задачи один
            и в одну строку (Enter здесь заканчивает правку, а не переносит),
            поэтому `input` подходит лучше — он и высоту берёт от общей строки
            для полей ввода.
          */}
          <div className="field">
            <label className="field__label" htmlFor={`title-${task.id}`}>
              Название
            </label>
            <div className="panel__titlerow">
              <input
                id={`title-${task.id}`}
                className="panel__title"
                value={title.draft}
                onChange={(event) => title.change(event.target.value)}
                onBlur={title.flush}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    title.flush();
                    event.currentTarget.blur();
                  }
                }}
              />
              <span className="panel__pencil" aria-hidden="true">
                <Pencil />
              </span>
            </div>
          </div>

          <div className="field">
            <span className="field__label">Статус</span>
            <div className="seg" role="group" aria-label="Статус задачи">
              {TASK_STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  /* Цвет нажатой кнопки зависит от статуса, см. `.seg button[data-status]`. */
                  data-status={status}
                  aria-pressed={task.status === status}
                  onClick={() => {
                    // Нажатие на уже выбранный статус ничего не меняет — и
                    // откликаться на него нечем.
                    if (task.status === status) return;
                    haptic();
                    actions.setStatus(task, status);
                  }}
                >
                  {STATUS_LABELS[status]}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field__label">Спринт</span>
            {/* Повтор — рядом со спринтом: это копия в каждом новом спринте (3.19.1). */}
            <div className="sprintrow">
              <SprintPicker
                task={task}
                current={currentSprint}
                onPick={(sprint) => actions.setSprint(task, sprint)}
              />
              {repeatAvailable(task, tasks) && (
                <RepeatMenu
                  task={task}
                  sprintType={sprintTypeOf(currentSprint)}
                  onChange={(repeat) => actions.setRepeat(task, repeat)}
                />
              )}
            </div>
            {repeatAvailable(task, tasks) && (
              <RepeatNext task={task} sprintType={sprintTypeOf(currentSprint)} />
            )}
          </div>

          <div className="field">
            <label className="field__label" htmlFor={`notes-${task.id}`}>
              Описание
            </label>
            <textarea
              id={`notes-${task.id}`}
              className="panel__notes"
              placeholder="Детали, ссылки, шаги"
              value={notes.draft}
              onChange={(event) => notes.change(event.target.value)}
              onBlur={notes.flush}
            />
          </div>

          <TagField
            tags={tags}
            allTags={allTags}
            onAdd={(raw) => actions.addTag(task.id, raw)}
            onRemove={actions.removeTag}
          />

          <DueField
            task={task}
            today={today}
            onChange={(due, dueTime) => actions.setDue(task, due, dueTime)}
          />

          <Checklist
            items={checklist}
            onAdd={(text, after) => actions.addChecklistItem(task.id, text, after)}
            onToggle={actions.toggleChecklistItem}
            onText={actions.setChecklistText}
            onRemove={actions.removeChecklistItem}
          />

          <Relations
            // Перезаводится с задачей: раскрытые нажатием пустые разделы связей
            // принадлежат этой карточке, а не следующей.
            key={task.id}
            task={task}
            tasks={tasks}
            links={links}
            byId={byId}
            actions={actions}
            onOpenTask={onOpenTask}
            onOpenProject={onOpenProject}
          />

          <Stamps task={task} />
        </div>

        {/*
          Кнопки «Закрыть» здесь нет намеренно: закрыть панель можно крестиком,
          подложкой и Esc, а четвёртый способ с тем же именем делает две
          одинаково названные кнопки на одном экране — в озвучке они
          неразличимы. Внизу остаётся только то, что закрытием не является.
        */}
        {/*
          Кнопки «Сохранить» здесь по-прежнему нет и не будет: правка уходит
          сама (3.5.2, принцип 5), а кнопка, которая ничего не сохраняет, врёт
          о том, что делает. Но два вопроса, ради которых её просили, —
          настоящие: «куда жать, чтобы выйти» и «приняты ли изменения».

          Отвечают на них выход, видимый там, где человек дочитал карточку, и
          живой индикатор состояния рядом с ним. Удаление отодвинуто в
          противоположный край: раньше единственной кнопкой внизу длинной
          карточки была красная, то есть ближайшей к пальцу — необратимая.
        */}
        <footer className={`panel__foot${typing ? ' panel__foot--typing' : ''}`}>
          <button type="button" className="finish" onClick={onClose}>
            К задачам
          </button>
          <SyncStatus label="Сохранение правок" compact />
          <span className="field__spacer" />
          <DeleteButton onDelete={() => actions.remove(task)} />
        </footer>
      </aside>
    </div>
  );
}

/**
 * Теги: ноль и больше, добавляются и снимаются в один клик (3.5).
 *
 * Пока поле нового тега в фокусе, под ним всплывает список существующих
 * тегов (3.10.5): самые ходовые, пока поле пустое, и подходящие под набранное,
 * когда человек печатает. Список отвечает на вопрос «нет ли уже такого?»
 * раньше, чем заведён двойник. Стоящие на задаче в нём не показываются
 * (3.10.6), а когда предложить нечего, списка нет вовсе (3.5.4).
 *
 * Список именно по фокусу, а не на виду всегда: первая версия держала ряд
 * чипов под полем постоянно, и владелец решил иначе (2026-09-08) — нажал в
 * поле, поднялась клавиатура, и вместе с ней список; ушёл из поля — список
 * пропал. Всплывает поверх того, что ниже, а не раздвигает карточку: иначе
 * при каждом фокусе всё под полем прыгало бы вниз.
 *
 * Механика фокуса — та же, что у встроенного подборщика задач: нажатие по
 * варианту само снимало бы фокус с поля, и список исчезал бы раньше, чем
 * срабатывал выбор. Поэтому `mousedown` внутри списка отменяется — фокус
 * остаётся в поле, а нажатие доходит до кнопки. Это же даёт «выбрал —
 * выбирай следующий»: после выбора клавиатура не прячется, список обновляется.
 */
function TagField({
  tags,
  allTags,
  onAdd,
  onRemove,
}: {
  tags: readonly TaskTag[];
  allTags: readonly TagCount[];
  onAdd: (raw: string) => void;
  onRemove: (tag: TaskTag) => void;
}): React.ReactElement {
  const [text, setText] = useState('');
  const [active, setActive] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const combo = useRef<HTMLDivElement>(null);
  const hints = active
    ? suggestTags(
        allTags,
        tags.map((tag) => tag.tag ?? ''),
        text,
      )
    : [];
  const open = hints.length > 0;

  // Список стоит под полем, а поле — в нижней трети длинной карточки: без
  // прокрутки на телефоне клавиатура накрыла бы его целиком. Вызов
  // необязательный: в jsdom метода нет (см. подборщик задач).
  useEffect(() => {
    if (open) combo.current?.scrollIntoView?.({ block: 'start' });
  }, [open]);

  function add(raw: string): void {
    onAdd(raw);
    setText('');
  }

  return (
    <div className="field">
      <span className="field__label">Теги</span>
      <div className="field__row">
        {tags.map((tag) => (
          <span key={tag.id} className="tag tag--editable">
            #{tag.tag}
            <button
              type="button"
              aria-label={`Убрать тег ${tag.tag ?? ''}`}
              onClick={() => onRemove(tag)}
            >
              <Times />
            </button>
          </span>
        ))}
        <div
          className="tag__combo"
          ref={combo}
          onMouseDown={(event) => {
            // Фокус остаётся в поле: только так `blur` означает «человек
            // ушёл», а не «человек выбирает из списка».
            if (event.target !== input.current) event.preventDefault();
          }}
        >
          <form
            className="tag__add"
            onSubmit={(event) => {
              event.preventDefault();
              if (text.trim().length === 0) return;
              add(text);
            }}
          >
            <input
              ref={input}
              aria-label="Новый тег"
              placeholder="тег"
              // Подсказки браузера из чужих полей легли бы поверх нашего
              // списка и читались бы как он (см. подборщик задач).
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={text}
              onFocus={() => setActive(true)}
              onBlur={() => setActive(false)}
              onChange={(event) => {
                setText(event.target.value);
                // Набор после Esc открывает список заново: фокус из поля не
                // уходил, и `focus` второй раз не случится.
                setActive(true);
              }}
              onKeyDown={(event) => {
                // Esc с раскрытым списком закрывает список, а не карточку;
                // без списка — как везде, карточку.
                if (event.key === 'Escape' && open) {
                  event.stopPropagation();
                  setActive(false);
                }
              }}
            />
            <button type="submit" className="miniadd" aria-label="Добавить тег">
              {/* Иконка, а не символ «+»: в чек-листе рядом стоит иконка, и два
                  разных плюса на одном экране читаются как разные кнопки. */}
              <Plus />
            </button>
          </form>
          {open && (
            <div className="tag__menu">
              {/* Без подписи список под полем читается как подсказка браузера. */}
              <span className="picker__caption">Существующие теги</span>
              <ul className="picker__list" aria-label="Существующие теги">
                {hints.map((tag) => (
                  <li key={tag}>
                    <button
                      type="button"
                      className="picker__item"
                      // Имя отличается и от «Добавить тег» у кнопки рядом с
                      // полем, и от «Убрать тег …» у стоящих: в озвучке три
                      // ряда кнопок с тегами иначе неразличимы.
                      aria-label={`Поставить тег ${tag}`}
                      onClick={() => add(tag)}
                    >
                      #{tag}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const STAMP_FORMAT = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Отметка времени или ничего.
 *
 * `Intl.format` от `Invalid Date` не возвращает мусор, а бросает `RangeError`,
 * и в React это уносит с собой всё дерево — страница гаснет. Дата при этом
 * пропадает не по чьей-то небрежности: у записей, заведённых до этапа 10,
 * `created_at` есть в базе, но без часов в `field_clocks`, а слияние копирует
 * только поля с часами (см. `mergeRecord`). То есть локальная копия таких дат
 * не знает и знать не будет.
 */
function formatStamp(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : STAMP_FORMAT.format(date);
}

/**
 * Видно, когда задача создана, изменена и закрыта (3.5, строка «Даты»).
 *
 * Строка показывается только если дата есть: пустое «создана —» сообщает не
 * больше, чем её отсутствие, зато выглядит как поломка.
 */
function Stamps({ task }: { task: Task }): React.ReactElement | null {
  const lines = [
    { key: 'created', label: 'создана', value: formatStamp(task.created_at) },
    { key: 'updated', label: 'изменена', value: formatStamp(task.updated_at) },
    { key: 'done', label: 'закрыта', value: formatStamp(task.done_at) },
  ].filter((line) => line.value !== undefined);

  if (lines.length === 0) return null;

  return (
    /*
      Сетка в две колонки, а не строка во всю ширину: строка переносилась где
      придётся, и три даты читались как одна фраза без начала. Раскладка —
      решение владельца (2026-09-08): «создана» слева, «изменена» справа,
      «закрыта» слева под «создана». Место каждой даты задано в стилях по её
      имени, поэтому пропавшая дата не сдвигает остальные.
    */
    <p className="panel__stamps">
      {lines.map((line) => (
        <span key={line.key} className={`panel__stamp panel__stamp--${line.key}`}>
          {line.label} {line.value}
        </span>
      ))}
    </p>
  );
}

/**
 * Удаление в два шага.
 *
 * Подтверждение заведено, когда корзины ещё не было и промах по кнопке был
 * необратим для человека, даже будучи обратимым для базы. Корзина появилась
 * (3.18.6), а шаг остался: она лежит в подвале, и промах без подтверждения
 * стоил бы похода за задачей туда — то есть был бы обратим, но не бесплатен.
 */
function DeleteButton({ onDelete }: { onDelete: () => void }): React.ReactElement {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      className="danger"
      onClick={() => {
        // Щелчок — только на втором, настоящем удалении: первое нажатие
        // ничего не делает, кроме вопроса.
        if (armed) {
          haptic();
          onDelete();
        } else setArmed(true);
      }}
    >
      {/* Короче обычной подписи: «Точно удалить задачу?» не помещалось в
          подвал и ломало его на два ряда. */}
      {armed ? 'Точно удалить?' : 'Удалить задачу'}
    </button>
  );
}
